import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { useI18n } from "@/lib/i18n";

export function StatCard({
  label, value, hint, icon: Icon, accent, onClick,
}: { label: string; value: string | number; hint?: string; icon?: LucideIcon; accent?: "verified" | "alert" | "default"; onClick?: () => void }) {
  const acc = accent === "verified" ? "border-l-verified" : accent === "alert" ? "border-l-alert" : "border-l-foreground";
  const Wrapper = onClick ? "button" : "div";
  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`w-full rounded-md border border-border bg-card p-5 border-l-4 text-left ${acc} shadow-card ${onClick ? "hover:bg-surface transition-colors cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-2 font-serif text-3xl font-semibold tracking-tight text-foreground">{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </Wrapper>
  );
}

export function EditorialStatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const config: Record<string, { labelKey: string; className: string }> = {
    PUBLIE:      { labelKey: "status.published", className: "bg-verified/15 text-verified border border-verified/30" },
    Publié:      { labelKey: "status.published", className: "bg-verified/15 text-verified border border-verified/30" },
    BROUILLON:   { labelKey: "status.draft",     className: "bg-amber-500/15 text-amber-600 border border-amber-500/30" },
    Brouillon:   { labelKey: "status.draft",     className: "bg-amber-500/15 text-amber-600 border border-amber-500/30" },
    EN_REVISION: { labelKey: "status.inReview",  className: "bg-blue-500/15 text-blue-600 border border-blue-500/30" },
    "En révision":{ labelKey: "status.inReview", className: "bg-blue-500/15 text-blue-600 border border-blue-500/30" },
    "En vérification":{ labelKey: "status.inReview", className: "bg-blue-500/15 text-blue-600 border border-blue-500/30" },
    EN_ATTENTE:  { labelKey: "status.pending",   className: "bg-orange-500/15 text-orange-600 border border-orange-500/30" },
    "En attente":{ labelKey: "status.pending",   className: "bg-orange-500/15 text-orange-600 border border-orange-500/30" },
    PROGRAMME:   { labelKey: "status.scheduled", className: "bg-indigo-500/15 text-indigo-600 border border-indigo-500/30" },
    "Programmé": { labelKey: "status.scheduled", className: "bg-indigo-500/15 text-indigo-600 border border-indigo-500/30" },
    ARCHIVE:     { labelKey: "status.archived",  className: "bg-alert/15 text-alert border border-alert/30" },
    Archivé:     { labelKey: "status.archived",  className: "bg-alert/15 text-alert border border-alert/30" },
    REJETE:      { labelKey: "status.rejected",  className: "bg-muted text-muted-foreground border border-border" },
    REFUSE:      { labelKey: "status.rejected",  className: "bg-muted text-muted-foreground border border-border" },
    "Refusé":    { labelKey: "status.rejected",  className: "bg-muted text-muted-foreground border border-border" },
    Rendu:       { labelKey: "status.rendered",  className: "bg-blue-500/15 text-blue-600 border border-blue-500/30" },
    Prêt:        { labelKey: "status.ready",     className: "bg-verified/15 text-verified border border-verified/30" },
  };

  const c = config[status];

  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${c ? c.className : "bg-surface text-muted-foreground border border-border"}`}
    >
      {c ? t(c.labelKey) : status}
    </span>
  );
}

export function ModulePanel({ title, description, children, action }: { title: string; description?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="rounded-md border border-border bg-card shadow-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold tracking-tight text-foreground">{title}</h2>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

export function DataTable({ columns, rows }: { columns: string[]; rows: (string | ReactNode)[][] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            {columns.map((c) => <th key={c} className="px-3 py-2">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-rule last:border-0 hover:bg-surface/60">
              {row.map((cell, j) => <td key={j} className="px-3 py-2.5 align-middle">{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
